import { FormWidgetProps, useMelioIntl } from '@melio/ar-domain';
import { Form, useMelioForm } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { object, SchemaOf, string } from 'yup';

import { CardHolderDetails } from '../types';

const useSchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    email: string()
      .email()
      .trim()
      .required(formatMessage('ar.dashboard.activities.cardHolder.form.fields.email.required.text')),
    firstName: string()
      .required(formatMessage('ar.dashboard.activities.cardHolder.form.fields.firstName.required.text'))
      .min(2, formatMessage('ar.dashboard.activities.cardHolder.form.fields.firstName.minChars.text')),
    lastName: string()
      .required(formatMessage('ar.dashboard.activities.cardHolder.form.fields.lastName.required.text'))
      .min(2, formatMessage('ar.dashboard.activities.cardHolder.form.fields.lastName.minChars.text')),
    postalCode: string()
      .required(formatMessage('ar.dashboard.activities.cardHolder.form.fields.postalCode.required.text'))
      .matches(/^\d{5}$/, formatMessage('ar.dashboard.activities.cardHolder.form.fields.postalCode.length.text')),
  }) as SchemaOf<CardHolderDetails>;
};

const ZIP_CODE_SHORT_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/];

export type CardHolderDetailsFormProps = FormWidgetProps<CardHolderDetails>;

export const CardHolderDetailsForm = forwardRef<CardHolderDetailsFormProps, 'form'>(
  ({ onSubmit, onSubmissionStateChange, isSaving, ...props }, ref) => {
    const { formatMessage } = useMelioIntl();
    const { formProps, registerField } = useMelioForm<CardHolderDetails>({
      onSubmit,
      schema: useSchema(),
      isSaving,
      onSubmissionStateChange,
    });

    return (
      <Form
        data-component="CardHolderDetailsForm"
        data-testid="card-holder-details-form"
        {...props}
        {...formProps}
        columns={2}
        ref={ref}
      >
        <Form.TextField
          colSpan={2}
          labelProps={{ label: formatMessage('ar.dashboard.activities.cardHolder.form.fields.email.label') }}
          {...registerField('email')}
        />
        <Form.TextField
          labelProps={{ label: formatMessage('ar.dashboard.activities.cardHolder.form.fields.firstName.label') }}
          {...registerField('firstName')}
        />
        <Form.TextField
          labelProps={{ label: formatMessage('ar.dashboard.activities.cardHolder.form.fields.lastName.label') }}
          {...registerField('lastName')}
        />
        <Form.TextField
          colSpan={2}
          labelProps={{ label: formatMessage('ar.dashboard.activities.cardHolder.form.fields.postalCode.label') }}
          maskProps={{ mask: ZIP_CODE_SHORT_MASK }}
          {...registerField('postalCode')}
        />
      </Form>
    );
  }
);
CardHolderDetailsForm.displayName = 'CardHolderDetailsForm';
